import { useContext, useEffect, useState } from 'react';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import useFetchPartnerAccounts from './useFetchPartnerAccounts';

type AccountBearer = {
  /** The account's token; empty until the lookup resolves, and after one that produced none. */
  bearer: string;
  /**
   * False only while the fallback lookup is in flight. A caller that waits on the token needs this
   * to tell "still looking" from "looked, and there is no token" — without it an account whose
   * lookup fails or returns no credentials leaves the caller waiting for good.
   */
  hasSettled: boolean;
};

/**
 * The bearer token for the given accountId. Uses AccountContext when the account is already loaded
 * (e.g. navigated from the accounts list), and falls back to fetching the account by VopayAccountID
 * when the page is loaded directly (a deep link, or a paginated list that didn't include it).
 */
export default function useAccountBearer(accountId: string): AccountBearer {
  const { details } = useContext(AccountContext);
  const contextToken = details[accountId]?.token ?? '';
  const [lookup, setLookup] = useState({ hasSettled: false, token: '' });
  const { fetchPartnerAccounts } = useFetchPartnerAccounts();

  useEffect(() => {
    // A settled lookup belongs to the account it was made for; the same instance serves the next one
    // when the page is not remounted. Left alone it would hand out the previous account's token.
    setLookup((current) => (current.hasSettled ? { hasSettled: false, token: '' } : current));

    if (contextToken || !accountId) return;

    // The token is read straight off the response rather than from hook state, so the lookup does
    // not need a second effect to notice it landed. A failure settles with no token — the hook has
    // logged the cause — which is what tells the caller to stop waiting.
    fetchPartnerAccounts({ VopayAccountID: accountId, Limit: 1 })
      .then((response) => setLookup({ hasSettled: true, token: response.accountDetails?.[accountId]?.token ?? '' }))
      .catch(() => setLookup({ hasSettled: true, token: '' }));
  }, [accountId, contextToken]);

  return {
    bearer: contextToken || lookup.token,
    hasSettled: Boolean(contextToken) || !accountId || lookup.hasSettled,
  };
}
